import { useState, useEffect, useRef } from 'react'
import { Routes, Route, Link, useLocation, useParams, useNavigate } from 'react-router-dom'
import { productService } from './utils/productService.js'
import ScrollToTop from './components/ScrollToTop.jsx'
import CartIcon from './components/CartIcon.jsx'
import CartPage from './components/CartPage.jsx'
import CheckoutPage from './components/CheckoutPage.jsx'
import { CartProvider, useCart } from './contexts/CartContext.jsx'
import './App.css'

// Product Link Component with referrer tracking
function ProductLink({ to, children, referrer }) {
  return (
    <Link 
      to={to} 
      state={{ referrer: referrer || window.location.pathname }}
      className="product-card-link"
    >
      {children}
    </Link>
  )
}

// Hoodies product data
const hoodiesData = [
  {
    title: "Eternal Soul Classic Hoodie",
    price: "54.99",
    description: "Classic fit hoodie with minimalist Eternal Soul branding.",
    primaryImage: "/images/S_NOBG.png",
    hoverImage: "/images/S.png"
  },
  {
    title: "Spiritual Warrior Hoodie",
    price: "64.99",
    description: "Bold graphic hoodie for the modern spiritual warrior.",
    primaryImage: "/images/S.png",
    hoverImage: "/images/S_NOBG.png"
  },
  {
    title: "Zen Collection Hoodie",
    price: "59.99",
    description: "Peaceful design perfect for meditation and mindfulness.",
    primaryImage: "/images/S_NOBG.png",
    hoverImage: "/images/S.png"
  },
  {
    title: "Mystic Moon Hoodie",
    price: "63.99",
    description: "Celestial inspired design with cosmic energy.",
    primaryImage: "/images/S.png",
    hoverImage: "/images/S_NOBG.png"
  },
  {
    title: "Sacred Geometry Hoodie",
    price: "60.99",
    description: "Mathematical patterns that connect mind, body, and spirit.",
    primaryImage: "/images/S_NOBG.png",
    hoverImage: "/images/S.png"
  },
  {
    title: "Chakra Balance Hoodie",
    price: "58.99",
    description: "Seven chakra colors in a beautiful gradient design.",
    primaryImage: "/images/S.png",
    hoverImage: "/images/S_NOBG.png"
  },
  {
    title: "Lotus Bloom Hoodie",
    price: "62.99",
    description: "Elegant lotus flower design symbolizing growth and enlightenment.",
    primaryImage: "/images/S_NOBG.png",
    hoverImage: "/images/S.png"
  }
]

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isShopOpen, setIsShopOpen] = useState(false)
  const location = useLocation()

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const toggleShop = () => {
    setIsShopOpen(!isShopOpen)
  }

  const closeMenus = () => {
    setIsMenuOpen(false)
    setIsShopOpen(false)
  }

  return (
    <nav className="navigation">
      <div className="nav-container">
        {/* Hamburger Menu Button - Mobile Only */}
        <button className="hamburger-btn" onClick={toggleMenu} aria-label="Toggle menu">
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
        </button>

        <div className="nav-brand">
          <img src={`/images/S_NOBG.png?v=2`} alt="Logo" className="nav-logo" />
        </div>
        
        {/* Cart icon moved to bottom left corner for web */}
        
        {/* Desktop Menu */}
        <ul className="nav-menu desktop-menu">
          <li className="shop-item">
            <button className="shop-toggle" onClick={toggleShop}>
              Shop
            </button>
            <ul className={`shop-dropdown ${isShopOpen ? 'open' : ''}`}>
              <li><Link to="/tshirts" onClick={closeMenus}>T Shirts</Link></li>
              <li><Link to="/hoodies" onClick={closeMenus}>Hoodies</Link></li>
              <li><Link to="/shop" onClick={closeMenus}>Shop All</Link></li>
            </ul>
          </li>
          <li><Link to="/" onClick={closeMenus}>Home</Link></li>
          <li><Link to="/about" onClick={closeMenus}>About</Link></li>
          <li><Link to="/contact" onClick={closeMenus}>Contact</Link></li>
        </ul>

        {/* Mobile Menu Backdrop */}
        {isMenuOpen && (
          <div className="mobile-menu-backdrop" onClick={toggleMenu}></div>
        )}

        {/* Mobile Menu */}
        <ul className={`nav-menu mobile-menu ${isMenuOpen ? 'open' : ''}`}>
          <li className="shop-item">
            <button className="shop-toggle" onClick={toggleShop}>
              Shop
            </button>
            <ul className={`shop-dropdown ${isShopOpen ? 'open' : ''}`}>
              <li><Link to="/tshirts" onClick={closeMenus}>T Shirts</Link></li>
              <li><Link to="/hoodies" onClick={closeMenus}>Hoodies</Link></li>
              <li><Link to="/shop" onClick={closeMenus}>Shop All</Link></li>
            </ul>
          </li>
          <li><Link to="/" onClick={closeMenus}>Home</Link></li>
          <li><Link to="/about" onClick={closeMenus}>About</Link></li>
          <li><Link to="/contact" onClick={closeMenus}>Contact</Link></li>
        </ul>
      </div>
    </nav>
  )
}

function RedTagBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })

  useEffect(() => {
    // Set target date to November 7th, 2025 at 6:00pm CST
    const targetDate = new Date('2025-11-07T18:00:00-06:00') // CST is UTC-6

    const timer = setInterval(() => {
      const now = new Date().getTime()
      const distance = targetDate.getTime() - now

      if (distance > 0) {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24))
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((distance % (1000 * 60)) / 1000)

        setTimeLeft({ days, hours, minutes, seconds })
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const toggleBanner = () => {
    setIsVisible(!isVisible)
  }

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  if (!isVisible) return null

  return (
    <>
      <div className="red-tag-banner">
        <div className="banner-content clickable" onClick={toggleDropdown}>
          <span className="banner-text">RED TAG NOTICE</span>
          <span className="countdown">
            {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
          </span>
        </div>
        <button className="banner-close" onClick={toggleBanner} aria-label="Close banner">
          ×
        </button>
      </div>
      
      {isDropdownOpen && (
        <div className="red-tag-dropdown">
          <div className="dropdown-content">
            <button className="dropdown-close" onClick={toggleDropdown} aria-label="Close dropdown">
              ×
            </button>
            <h1 className="dropdown-title">RED TAG DROP</h1>
            <p className="dropdown-subtitle">
              This is a limited edition release which will only be available for one day (November 7th)
            </p>
            <div className="dropdown-countdown">
              <span className="countdown-label">Time Remaining:</span>
              <span className="countdown-timer">
                {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
              </span>
            </div>
            <div className="dropdown-hero-placeholder">
              {/* Hero image will go here */}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function ProductCard({ title, price, description, primaryImage, hoverImage, category, slug }) {
  // Make cart functionality optional to prevent crashes when CartProvider is not available
  let addToCart = null
  try {
    const cartContext = useCart()
    addToCart = cartContext.addToCart
  } catch (error) {
    // CartProvider not available, cart functionality will be disabled
    addToCart = null
  }
  
  const [selectedSize, setSelectedSize] = useState('')
  const [showSizeSelector, setShowSizeSelector] = useState(false)

  const sizes = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL']

  const handleAddToCart = (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!addToCart) {
      alert('Cart functionality not available')
      return
    }
    
    if (!selectedSize) {
      setShowSizeSelector(true)
      return
    }

    const product = {
      slug,
      title,
      price,
      primaryImage,
      category
    }

    addToCart(product, selectedSize)
    setShowSizeSelector(false)
    setSelectedSize('')
  }

  return (
    <div className="product-card" data-category={category} data-slug={slug}>
      {category && slug ? (
        <ProductLink to={`/shop/${category}/${slug}`}>
          <div className="product-image">
            {primaryImage && (
              <img 
                src={primaryImage} 
                alt={title}
                className="product-primary-image"
              />
            )}
            {hoverImage && (
              <img 
                src={hoverImage} 
                alt={`${title} - Hover view`}
                className="product-hover-image"
              />
            )}
          </div>
          <div className="product-info">
            <h3 className="product-title">{title}</h3>
            <div className="product-price">${price}</div>
          </div>
        </ProductLink>
      ) : (
        <>
          <div className="product-image">
            {primaryImage && (
              <img 
                src={primaryImage} 
                alt={title}
                className="product-primary-image"
              />
            )}
            {hoverImage && (
              <img 
                src={hoverImage} 
                alt={`${title} - Hover view`}
                className="product-hover-image"
              />
            )}
          </div>
          <div className="product-info">
            <h3 className="product-title">{title}</h3>
            <div className="product-price">${price}</div>
          </div>
        </>
      )}
      
      {/* Quick Add to Cart - only show if cart functionality is available */}
      {addToCart && (
        <div className="product-actions">
          {showSizeSelector ? (
            <div className="size-selector">
              <div className="size-options">
                {sizes.map(size => (
                  <button
                    key={size}
                    className={`size-btn ${selectedSize === size ? 'selected' : ''}`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <div className="size-selector-actions">
                <button 
                  className="add-to-cart-btn"
                  onClick={handleAddToCart}
                  disabled={!selectedSize}
                >
                  Add to Cart
                </button>
                <button 
                  className="cancel-btn"
                  onClick={() => {
                    setShowSizeSelector(false)
                    setSelectedSize('')
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button 
              className="add-to-cart-btn"
              onClick={handleAddToCart}
            >
              Quick Add
            </button>
          )}
        </div>
      )}
    </div>
  )
}


// Dynamic Product Detail Page Component
function DynamicProductDetailPage() {
  const { category, productSlug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  
  const product = productService.getProduct(category, productSlug)
  const referrer = location.state?.referrer || '/shop'
  
  if (!product) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem 0' }}>
        <h1>Product Not Found</h1>
        <p>The product you're looking for doesn't exist.</p>
        <button 
          onClick={() => navigate('/shop')} 
          className="add-to-cart-btn"
          style={{ marginTop: '1rem' }}
        >
          Back to Shop
        </button>
      </div>
    )
  }
  
  const handleBack = () => {
    navigate(referrer)
  }

  return <ProductDetailPage product={product} onBack={handleBack} />
}

// Product Detail Page Component
function ProductDetailPage({ product, onBack }) {
  const [selectedSize, setSelectedSize] = useState('')
  const [selectedColor, setSelectedColor] = useState('')
  const [selectedImage, setSelectedImage] = useState(0)
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [slideDirection, setSlideDirection] = useState('')
  const [nextImage, setNextImage] = useState(null)
  const [hasRevealed, setHasRevealed] = useState(false)
  
  // Cart functionality
  let addToCart = null
  try {
    const cartContext = useCart()
    addToCart = cartContext.addToCart
  } catch (error) {
    // CartProvider not available, cart functionality will be disabled
    addToCart = null
  }

  // Run pixelated reveal only on initial mount
  useEffect(() => {
    setHasRevealed(true)
  }, [])
  
  // Use product's actual sizes, fallback to default if not specified
  const sizes = product.sizes || ['S', 'M', 'L', 'XL', 'XXL']
  const availableSizes = product.sizes || ['S', 'M', 'L', 'XL', 'XXL']
  
  // Use product's available colors, fallback to default if not specified
  const colors = product.colors || ['Black']
  const availableColors = product.colors || ['Black']
  
  // Use color-specific images if available and color is selected, otherwise use default detail images
  const getImages = () => {
    if (product.colorImages && selectedColor && product.colorImages[selectedColor]) {
      return product.colorImages[selectedColor]
    }
    return product.detailImages || [
      product.primaryImage,
      product.hoverImage
    ].filter(Boolean)
  }
  
  const images = getImages()

  // Default color to Black (or first available) so only size selection gates Add to Cart
  useEffect(() => {
    if (!selectedColor && availableColors && availableColors.length > 0) {
      const preferred = availableColors.includes('Black') ? 'Black' : availableColors[0]
      setSelectedColor(preferred)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableColors])

  // Canvas-based watermark flattening cache
  const watermarkCacheRef = useRef(new Map())
  const [displayImages, setDisplayImages] = useState(images)

  // Helper to flatten watermark onto image using canvas
  const flattenWatermark = async (imageUrl) => {
    const cache = watermarkCacheRef.current
    if (cache.has(imageUrl)) return cache.get(imageUrl)
    try {
      const [img, wm] = await Promise.all([
        new Promise((res, rej) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => res(i); i.onerror = rej; i.src = imageUrl }),
        new Promise((res, rej) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => res(i); i.onerror = rej; i.src = '/images/watermark.png' })
      ])
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth || img.width
      canvas.height = img.naturalHeight || img.height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      // Draw watermark centered and scaled to fit nicely (contain)
      const wmRatio = wm.width / wm.height
      const targetWidth = canvas.width * 0.8
      const targetHeight = targetWidth / wmRatio
      const x = (canvas.width - targetWidth) / 2
      const y = (canvas.height - targetHeight) / 2
      // For any inverted graphics, tint watermark black for better contrast
      const useBlackTint = /inverted/i.test(imageUrl)
      if (useBlackTint) {
        const wmCanvas = document.createElement('canvas')
        wmCanvas.width = targetWidth
        wmCanvas.height = targetHeight
        const wmCtx = wmCanvas.getContext('2d')
        // draw watermark scaled
        wmCtx.drawImage(wm, 0, 0, targetWidth, targetHeight)
        // tint to black
        wmCtx.globalCompositeOperation = 'source-in'
        wmCtx.fillStyle = '#000000'
        wmCtx.fillRect(0, 0, targetWidth, targetHeight)
        wmCtx.globalCompositeOperation = 'source-over'
        ctx.globalAlpha = 0.3
        ctx.drawImage(wmCanvas, x, y)
        ctx.globalAlpha = 1
      } else {
        ctx.globalAlpha = 0.3
        ctx.drawImage(wm, x, y, targetWidth, targetHeight)
        ctx.globalAlpha = 1
      }
      const dataUrl = canvas.toDataURL('image/png')
      cache.set(imageUrl, dataUrl)
      return dataUrl
    } catch {
      return imageUrl
    }
  }

  // Build displayImages by flattening watermark for graphic images
  useEffect(() => {
    let isActive = true
    const run = async () => {
      const processed = await Promise.all(
        images.map(async (url) => (hasWatermark(url) ? await flattenWatermark(url) : url))
      )
      if (isActive) setDisplayImages(processed)
    }
    run()
    return () => { isActive = false }
  }, [images])

  // Helper function to check if image should have watermark
  const hasWatermark = (imageSrc) => {
    return imageSrc && imageSrc.includes('graphic')
  }

  const handleSizeSelect = (size) => {
    if (availableSizes.includes(size)) {
      setSelectedSize(size)
    }
  }

  const handleColorSelect = (color) => {
    if (availableColors.includes(color)) {
      setSelectedColor(color)
      setSelectedImage(0) // Reset to first image when color changes
    }
  }

  // Preload and start slide transition
  const startSlideTransition = (targetIndex) => {
    const direction = targetIndex > selectedImage ? 'left' : 'right'
    const url = images[targetIndex]
    if (!url) return
    const img = new Image()
    img.onload = () => {
      setNextImage(url)
      setSlideDirection(direction)
      setIsTransitioning(true)
      setTimeout(() => {
        setSelectedImage(targetIndex)
        setNextImage(null)
        setIsTransitioning(false)
        setSlideDirection('')
      }, 300)
    }
    img.onerror = () => {
      // Fallback: switch without transition if preload fails
      setSelectedImage(targetIndex)
      setNextImage(null)
      setIsTransitioning(false)
      setSlideDirection('')
    }
    img.src = url
  }

  const handleImageSelect = (index) => {
    if (index === selectedImage || isTransitioning) return
    startSlideTransition(index)
  }

  // Swipe functionality
  const minSwipeDistance = 50

  const onTouchStart = (e) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && selectedImage < displayImages.length - 1) {
      startSlideTransition(selectedImage + 1)
    }
    if (isRightSwipe && selectedImage > 0) {
      startSlideTransition(selectedImage - 1)
    }
  }

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert('Please select a size')
      return
    }
    if (availableColors.length > 1 && !selectedColor) {
      alert('Please select a color')
      return
    }
    
    // Add to cart using the cart context
    if (addToCart) {
      addToCart(product, selectedSize, selectedColor || 'Black', 1)
    } else {
      console.error('Cart functionality not available')
    }
  }


  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product.title,
        text: product.description,
        url: window.location.href
      })
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href)
      alert('Product link copied to clipboard!')
    }
  }

  // Build 6 random recommended products excluding the current product
  const recommendedProducts = (() => {
    const all = productService.getAllProducts()
    const pool = all.filter(p => !(p.category === product.category && p.slug === product.slug))
    const shuffled = [...pool].sort(() => 0.5 - Math.random())
    return shuffled.slice(0, 6)
  })()

  return (
    <div className="product-detail-page" data-category={product.category}>
      {/* Back to Products Button */}
      {onBack && (
        <div className="product-detail-back">
          <button onClick={onBack} className="back-to-products-btn">
            ← Back to Products
          </button>
        </div>
      )}
      <div className="product-detail-main-section">
        <div className="product-detail-container">
        {/* Product Gallery */}
        <div className="product-detail-gallery">
          <div className="product-detail-thumbnails">
            {images.map((image, index) => (
              <div 
                key={index}
                className={`product-detail-thumbnail ${selectedImage === index ? 'active' : ''} ${hasWatermark(image) ? 'watermark-overlay' : ''}`}
                onClick={() => handleImageSelect(index)}
              >
                <img 
                  src={image} 
                  alt={`${product.title} view ${index + 1}`}
                />
              </div>
            ))}
          </div>
          {/** Determine watermark based on incoming image; skip overlay if watermark already flattened */}
          <div 
            className={`product-detail-main-image ${(() => {
              const currentNeedsWm = hasWatermark(images[selectedImage])
              const currentFlattened = currentNeedsWm && displayImages[selectedImage] && displayImages[selectedImage] !== images[selectedImage]
              const nextNeedsWm = hasWatermark(nextImage)
              const nextFlattened = nextNeedsWm && nextImage && watermarkCacheRef.current.get(nextImage)
              if (isTransitioning) {
                return nextNeedsWm && !nextFlattened ? 'watermark-overlay' : ''
              }
              return currentNeedsWm && !currentFlattened ? 'watermark-overlay' : ''
            })()}`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <img 
              src={displayImages[selectedImage]}
              alt={product.title}
              className={`${!hasRevealed ? 'pixelated-effect' : ''} current-image ${isTransitioning ? (slideDirection === 'left' ? 'slide-out-left' : 'slide-out-right') : ''}`}
            />
            {nextImage && (
              <img 
                src={hasWatermark(nextImage) ? (watermarkCacheRef.current.get(nextImage) || nextImage) : nextImage}
                alt={product.title}
                className={`next-image ${slideDirection === 'left' ? 'slide-in-left' : 'slide-in-right'}`}
              />
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="product-detail-info">
          <h1 className="product-detail-title">{product.title}</h1>
          <div className="product-detail-price">${product.price}</div>
          
          {/* Color Selection - Only show if multiple colors available */}
          {availableColors.length > 1 && (
            <div className="product-detail-colors">
              <h3>Color</h3>
              <div className="color-options">
                {colors.map((color) => (
                  <button
                    key={color}
                    className={`color-option ${
                      selectedColor === color ? 'selected' : ''
                    } ${
                      !availableColors.includes(color) ? 'unavailable' : ''
                    }`}
                    onClick={() => handleColorSelect(color)}
                    disabled={!availableColors.includes(color)}
                    style={{
                      backgroundColor: color === 'White' ? '#ffffff' : '#000000',
                      color: color === 'White' ? '#000000' : '#ffffff',
                      border: color === 'White' ? '2px solid #000000' : '2px solid #ffffff'
                    }}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="product-detail-description">{product.description}</p>

          {/* Size Selection */}
          <div className="product-detail-sizes">
            <h3>Size</h3>
            <div className="size-options">
              {sizes.map((size) => (
                <button
                  key={size}
                  className={`size-option ${
                    selectedSize === size ? 'selected' : ''
                  } ${
                    !availableSizes.includes(size) ? 'unavailable' : ''
                  }`}
                  onClick={() => handleSizeSelect(size)}
                  disabled={!availableSizes.includes(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="product-detail-actions">
            <button 
              className="product-detail-add-to-cart"
              onClick={handleAddToCart}
              disabled={!selectedSize || (availableColors.length > 1 && !selectedColor)}
            >
              Add to Cart
            </button>
            <div className="product-detail-secondary-actions">
              <button 
                className="product-detail-share"
                onClick={handleShare}
              >
                📤 Share
              </button>
            </div>
          </div>

          {/* Product Specifications */}
          <div className="product-detail-specs">
            <h3>Product Details</h3>
            <ul className="spec-list">
              <li>
                <span className="spec-label">Material:</span>
                <span className="spec-value">100% Premium Cotton</span>
              </li>
              <li>
                <span className="spec-label">Care Instructions:</span>
                <span className="spec-value">Machine wash cold, tumble dry low</span>
              </li>
              <li>
                <span className="spec-label">Origin:</span>
                <span className="spec-value">Made in USA</span>
              </li>
              <li>
                <span className="spec-label">Shipping:</span>
                <span className="spec-value">Free shipping on orders over $100</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      </div>

      {/* Recommended Products Section */}
      <div className="recommended-section">
        <div className="recommended-container">
          <h2 className="recommended-title">You Might Also Like</h2>
          <div className="carousel-wrap">
            <button
              className="carousel-arrow left"
              aria-label="Scroll left"
              onClick={() => {
                const el = document.getElementById('recommended-products')
                if (!el) return
                const amount = Math.max(300, Math.floor(el.clientWidth * 0.9))
                el.scrollBy({ left: -amount, behavior: 'smooth' })
              }}
            >
              ‹
            </button>
            <div className="recommended-grid" id="recommended-products">
              {recommendedProducts.map((rp, index) => (
                <ProductCard
                  key={`${rp.category}-${rp.slug}-${index}`}
                  title={rp.title}
                  price={rp.price}
                  description={rp.description}
                  primaryImage={rp.primaryImage}
                  hoverImage={rp.hoverImage}
                  category={rp.category}
                  slug={rp.slug}
                />
              ))}
            </div>
            <button
              className="carousel-arrow right"
              aria-label="Scroll right"
              onClick={() => {
                const el = document.getElementById('recommended-products')
                if (!el) return
                const amount = Math.max(300, Math.floor(el.clientWidth * 0.9))
                el.scrollBy({ left: amount, behavior: 'smooth' })
              }}
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function GenderCategories() {
  const categories = [
    {
      title: "T-shirts",
      image: "/images/mens_page.png",
      link: "/tshirts"
    },
    {
      title: "Hoodies",
      image: "/images/hoodie_page.png",
      link: "/hoodies"
    }
  ]

  return (
    <section className="gender-categories">
      <div className="container">
        <h2 className="section-title">Shop by Category</h2>
        <div className="categories-grid">
          {categories.map((category, index) => (
            <div key={index} className="category-card">
              {category.image && (
                <div className="category-image">
                  <img 
                    src={category.image} 
                    alt={category.title} 
                    style={category.title === 'Hoodies' ? { objectPosition: 'center 30%' } : undefined}
                  />
                </div>
              )}
              <div className="category-info">
                <h3 className="category-title">{category.title}</h3>
                <Link to={category.link} className="category-btn">Shop All</Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FanFavorites() {
  const favoriteSlugs = [
    'eternal-love',
    'eternal-sun',
    'unchained-soul',
    'boundless-soul',
    'classic-tee',
    'embrace-your-soul-hoodie'
  ]

  const allProducts = productService.getAllProducts()
  const fanFavorites = favoriteSlugs
    .map(slug => allProducts.find(p => p.slug === slug))
    .filter(Boolean)

  return (
    <section className="fan-favorites">
      <div className="container">
        <h2 className="section-title">Fan Favorites</h2>
        <div className="fan-favorites-grid">
          {fanFavorites.map((product, index) => (
            <ProductCard
              key={index}
              title={product.title}
              price={product.price}
              description={product.description}
              primaryImage={product.primaryImage}
              hoverImage={product.hoverImage}
              category={product.category}
              slug={product.slug}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

function FeaturedItems() {
  // Explicit Recent Drops selection in the order provided
  const recentDropSlugs = [
    'eternal-awakening',
    'eternal-evolution',
    'eternal-chaos-hoodie',  // 3rd position - hoodie
    'eternal-sun-hoodie',    // 4th position - hoodie
    'eternal-battle',
    'eternal-chaos',
    'woven-within',
    'eternal-rose'
  ]
  const allTshirts = productService.getProductsByCategory('tshirt')
  const allHoodies = productService.getProductsByCategory('hoodie')
  const allProducts = [...allTshirts, ...allHoodies]
  const products = recentDropSlugs
    .map(slug => allProducts.find(p => p.slug === slug))
    .filter(Boolean)

  return (
    <section className="featured-items">
        <div className="container">
          <h2 className="section-title">Recent Drops</h2>
          <div className="carousel-wrap">
            <button
              className="carousel-arrow left"
              aria-label="Scroll left"
              onClick={() => {
                const el = document.getElementById('recent-products')
                if (!el) return
                const amount = Math.max(300, Math.floor(el.clientWidth * 0.9))
                el.scrollBy({ left: -amount, behavior: 'smooth' })
              }}
            >
              ‹
            </button>
            <div className="products-grid" id="recent-products">
          {products.map((product, index) => (
            <ProductCard
              key={index}
              title={product.title}
              price={product.price}
              description={product.description}
              primaryImage={product.primaryImage}
              hoverImage={product.hoverImage}
              category={product.category}
              slug={product.slug}
            />
          ))}
            </div>
            <button
              className="carousel-arrow right"
              aria-label="Scroll right"
              onClick={() => {
                const el = document.getElementById('recent-products')
                if (!el) return
                const amount = Math.max(300, Math.floor(el.clientWidth * 0.9))
                el.scrollBy({ left: amount, behavior: 'smooth' })
              }}
            >
              ›
            </button>
          </div>
        <div className="scroll-indicator">
          <div className="scroll-indicator-bar">
            <div className="scroll-indicator-slider"></div>
          </div>
          <p className="scroll-indicator-text">← Swipe to see more →</p>
        </div>
      </div>
    </section>
  )
}

function HoodiesPage() {
  const hoodieProducts = productService.getProductsByCategory('hoodie')
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
    <>
      <header className="App-header">
        <div className="hero-image-container">
          <img 
            src={isMobile ? "/images/hoodies_hero_image_mobile.png" : "/images/hoodies_hero_image.png"} 
            alt="Hoodies Hero" 
            className="hero-image" 
          />
        </div>
      </header>
      <section className="hoodies-collection">
        <div className="container">
          <h2 className="section-title">Hoodies Collection</h2>
          <div className="hoodies-grid">
            {hoodieProducts.map((product, index) => (
              <ProductCard
                key={index}
                title={product.title}
                price={product.price}
                description={product.description}
                primaryImage={product.primaryImage}
                hoverImage={product.hoverImage}
                category={product.category}
                slug={product.slug}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

// Home Page Component
function HomePage() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <>
      <header className="App-header">
        <div className="hero-image-container">
          <img 
            src={isMobile ? "/images/wide_web_graphic_mobile.png" : "/images/wide_web_graphic.png"} 
            alt="Wide Web Graphic" 
            className="hero-image" 
          />
        </div>
      </header>
      <FeaturedItems />
      <FanFavorites />
      <GenderCategories />
    </>
  )
}

// About Page Component
function AboutPage() {
  return (
    <>
      <div className="container">
        <h1>About Us</h1>
        <p>Welcome to Eternal Soul - where fashion meets spirituality.</p>
      </div>
    </>
  )
}

// Shop Page Component
function ShopPage() {
  const allProducts = productService.getAllProducts().sort((a, b) => a.title.localeCompare(b.title))
  const totalProducts = allProducts.length
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState([])
  const filterRef = useRef(null)
  const [isMobile, setIsMobile] = useState(false)
  
  const filterOptions = [
    'T Shirt',
    'Hoodie', 
    'Signature Pieces',
    'Trending Now',
    'Classics'
  ]
  
  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen)
  }
  
  const handleFilterSelect = (filter) => {
    if (selectedFilters.includes(filter)) {
      setSelectedFilters(selectedFilters.filter(f => f !== filter))
    } else {
      setSelectedFilters([...selectedFilters, filter])
    }
  }
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false)
      }
    }
    
    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isFilterOpen])

  // Detect mobile to swap hero image source
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  return (
    <>
      <div className="shop-page">
        <header className="App-header">
          <div className="hero-image-container">
            <img 
              src={isMobile ? "/images/Goth_Soul.png" : "/images/wide_web_graphic.png"}
              alt="Shop Hero"
              className="hero-image"
            />
          </div>
        </header>
        <section className="shop-collection">
          <div className="container">
          <div className="shop-header">
            <div className="shop-controls">
              <div className="product-count">
                Showing {totalProducts} of {totalProducts} items
              </div>
              <div className="filter-container" ref={filterRef}>
                <button className="filter-btn" onClick={toggleFilter}>
                  <span className="filter-icon">⚙</span>
                  Filter
                  {selectedFilters.length > 0 && (
                    <span className="filter-count">{selectedFilters.length}</span>
                  )}
                </button>
                {isFilterOpen && (
                  <div className="filter-dropdown">
                    <div className="filter-dropdown-header">
                      <h3>Filter by Category</h3>
                      <button 
                        className="clear-filters-btn"
                        onClick={() => setSelectedFilters([])}
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="filter-options">
                      {filterOptions.map((option) => (
                        <label key={option} className="filter-option">
                          <input
                            type="checkbox"
                            checked={selectedFilters.includes(option)}
                            onChange={() => handleFilterSelect(option)}
                          />
                          <span className="filter-option-text">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="shop-grid">
            {allProducts.map((product, index) => (
              <ProductCard
                key={index}
                title={product.title}
                price={product.price}
                description={product.description}
                primaryImage={product.primaryImage}
                hoverImage={product.hoverImage}
                category={product.category}
                slug={product.slug}
              />
            ))}
          </div>
          </div>
        </section>
      </div>
    </>
  )
}

// Contact Page Component
function ContactPage() {
  return (
    <>
      <div className="container">
        <h1>Contact Us</h1>
        <p>Get in touch with us for any questions or support.</p>
      </div>
    </>
  )
}

// T-Shirts Page Component
function TShirtsPage() {
  const tshirtProducts = productService.getProductsByCategory('tshirt').sort((a, b) => a.title.localeCompare(b.title))
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
    <>
      <header className="App-header">
        <div className="hero-image-container">
          <img 
            src={isMobile ? "/images/tshirt_hero_image_mobile.png" : "/images/tshirt_hero_image.png"} 
            alt="T-Shirt Hero" 
            className="hero-image" 
          />
        </div>
      </header>
      <section className="tshirts-collection">
        <div className="container">
          <h2 className="section-title">T-Shirts Collection</h2>
          <div className="tshirts-grid">
            {tshirtProducts.map((product, index) => (
              <ProductCard
                key={index}
                title={product.title}
                price={product.price}
                description={product.description}
                primaryImage={product.primaryImage}
                hoverImage={product.hoverImage}
                category={product.category}
                slug={product.slug}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

// Collections Page Component
function CollectionsPage() {
  return (
    <>
      <header className="App-header">
        <div className="hero-image-container">
          <img src="/images/Goth_Soul.png" alt="Goth Soul" className="hero-image" />
        </div>
      </header>
      <FeaturedItems />
    </>
  )
}

// Template Page - displays a single product detail page directly
function TemplatePage() {
  const templateProduct = {
    title: "Geisha Collection Tee",
    price: "29.99",
    description: "Premium cotton t-shirt featuring our elegant geisha design. Perfect for those who appreciate the beauty of traditional artistry and modern spiritual expression. This template showcases the product detail page with size selection, pricing, and all the features you need for your e-commerce site.",
    primaryImage: "/images/geisha_mockup.png",
    hoverImage: "/images/geisha_graphic.png"
  }

  return <ProductDetailPage product={templateProduct} />
}

function App() {
  return (
    <CartProvider>
      <div className="App">
        <ScrollToTop />
        <Navigation />
        <RedTagBanner />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/:category/:productSlug" element={<DynamicProductDetailPage />} />
            <Route path="/template" element={<TemplatePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/tshirts" element={<TShirtsPage />} />
            <Route path="/hoodies" element={<HoodiesPage />} />
            <Route path="/collections" element={<CollectionsPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
          </Routes>
        </main>
        
        {/* Floating Cart Button - Web Only */}
        <Link to="/cart" className="floating-cart-btn">
          <CartIcon style="bag" />
        </Link>
      </div>
    </CartProvider>
  )
}

export default App
